export const ParamsFilterKey = {
  QUERY: "query",
  PAGE: "page",
  ROWS_PER_PAGE: "rowsPerPage",
  SENIORITIES: "seniorities",
  POSITIONS: "positions",
  CLASSIFICATIONS: "classifications",
  STATES: "states",
  /** use `id` */
  INSTITUTIONS: "institution_ids",
  DEPARTMENTS: "departments",
  KEYWORD_QUERY: "keywordQuery",
  TIME_FILTER: "timeFilter",
  LEVELS: "levels",
  TOP_K: "top_k",
  TOP_K_EDGES: "top_k_edges",
  KEYWORD_TIME_FILTER: "keywordTimeFilter",
  ORDER_BY: "orderBy",
  ORDER: "order",
  SCHOOL_TYPE: "schoolType",
  FULL_TEXT_SEARCH: "fullTextSearch",
};

export const KEYWORD_TIME_FILTER_KEYS = {
  all: "All",
  recent: "Recent",
};

export const DEFAULT_SEARCH_PARAMS = {
  page: 1,
  rowsPerPage: 30,
  top_k: 30,
  top_k_edges: 90,
  timeFilter: "all",
  query: null,
  seniorities: null,
  positions: null,
  classifications: null,
  states: null,
  institution_ids: null,
  departments: null,
  keywordQuery: null,
  levels: null,
  keywordTimeFilter: "all",
  orderBy: null,
  order: null,
  schoolType: null,
  fullTextSearch: null,
  advanced_search_input: null,
};

const joinQueryValues = (values) => values.join("|");
const splitQueryValues = (value) => value.split("|");

export const fromSearchParams = (searchParams, defaultFilter = {}) => {
  const filter = { ...DEFAULT_SEARCH_PARAMS, ...defaultFilter };
  for (const key of searchParams.keys()) {
    const value = searchParams.get(key);
    if (!value) continue;
    switch (key) {
      case ParamsFilterKey.QUERY:
      case ParamsFilterKey.SENIORITIES:
      case ParamsFilterKey.POSITIONS:
      case ParamsFilterKey.CLASSIFICATIONS:
      case ParamsFilterKey.STATES:
      case ParamsFilterKey.INSTITUTIONS:
      case ParamsFilterKey.DEPARTMENTS:
      case ParamsFilterKey.KEYWORD_QUERY:
      case ParamsFilterKey.LEVELS:
      case ParamsFilterKey.SCHOOL_TYPE:
        filter[key] = splitQueryValues(value);
        break;
      case ParamsFilterKey.FULL_TEXT_SEARCH:
        filter[key] = value;
        break;
      case ParamsFilterKey.PAGE:
      case ParamsFilterKey.ROWS_PER_PAGE:
      case ParamsFilterKey.TOP_K:
      case ParamsFilterKey.TOP_K_EDGES:
        filter[key] = +value || DEFAULT_SEARCH_PARAMS[key];
        break;
      default:
        filter[key] = value || DEFAULT_SEARCH_PARAMS[key];
    }
  }
  return filter;
};

export const toSearchParams = (filter, defaultFilter = {}) => {
  const defaultFilterValues = {
    ...DEFAULT_SEARCH_PARAMS,
    ...defaultFilter,
  };
  const searchParams = new URLSearchParams();
  if (Array.isArray(filter.query) && filter.query.length > 0) {
    searchParams.set(ParamsFilterKey.QUERY, joinQueryValues(filter.query));
  }
  if (typeof filter.query === "string") {
    searchParams.set(ParamsFilterKey.QUERY, [filter.query]);
  }
  if (
    typeof filter.page === "number" &&
    filter.page > defaultFilterValues.page
  ) {
    searchParams.set(ParamsFilterKey.PAGE, filter.page);
  }
  if (
    typeof filter.rowsPerPage === "number" &&
    filter.rowsPerPage !== defaultFilterValues.rowsPerPage
  ) {
    searchParams.set(ParamsFilterKey.ROWS_PER_PAGE, filter.rowsPerPage);
  }
  if (Array.isArray(filter.seniorities) && filter.seniorities.length > 0) {
    searchParams.set(
      ParamsFilterKey.SENIORITIES,
      joinQueryValues(filter.seniorities)
    );
  }
  if (Array.isArray(filter.positions) && filter.positions.length > 0) {
    searchParams.set(
      ParamsFilterKey.POSITIONS,
      joinQueryValues(filter.positions)
    );
  }
  if (
    Array.isArray(filter.classifications) &&
    filter.classifications.length > 0
  ) {
    searchParams.set(
      ParamsFilterKey.CLASSIFICATIONS,
      joinQueryValues(filter.classifications)
    );
  }
  if (Array.isArray(filter.states) && filter.states.length > 0) {
    searchParams.set(ParamsFilterKey.STATES, joinQueryValues(filter.states));
  }
  if (
    Array.isArray(filter[ParamsFilterKey.INSTITUTIONS]) &&
    filter[ParamsFilterKey.INSTITUTIONS].length > 0
  ) {
    searchParams.set(
      ParamsFilterKey.INSTITUTIONS,
      joinQueryValues(filter[ParamsFilterKey.INSTITUTIONS])
    );
  }
  if (Array.isArray(filter.departments) && filter.departments.length > 0) {
    searchParams.set(
      ParamsFilterKey.DEPARTMENTS,
      joinQueryValues(filter.departments)
    );
  }
  if (Array.isArray(filter.keywordQuery) && filter.keywordQuery.length > 0) {
    searchParams.set(
      ParamsFilterKey.KEYWORD_QUERY,
      joinQueryValues(filter.keywordQuery)
    );
  }
  if (typeof filter.keywordQuery === "string") {
    searchParams.set(ParamsFilterKey.KEYWORD_QUERY, [filter.keywordQuery]);
  }
  if (
    typeof filter.timeFilter === "string" &&
    filter.timeFilter !== defaultFilterValues.timeFilter
  ) {
    searchParams.set(ParamsFilterKey.TIME_FILTER, filter.timeFilter);
  }
  if (Array.isArray(filter.levels) && filter.levels.length > 0) {
    searchParams.set(ParamsFilterKey.LEVELS, joinQueryValues(filter.levels));
  }
  if (
    typeof filter.top_k === "number" &&
    filter.top_k !== defaultFilterValues.top_k
  ) {
    searchParams.set(ParamsFilterKey.TOP_K, filter.top_k);
  }
  if (
    typeof filter.top_k_edges === "number" &&
    filter.top_k_edges !== defaultFilterValues.top_k_edges
  ) {
    searchParams.set(ParamsFilterKey.TOP_K_EDGES, filter.top_k_edges);
  }
  if (
    !!filter.keywordTimeFilter &&
    filter.keywordTimeFilter !== defaultFilterValues.keywordTimeFilter
  ) {
    searchParams.set(
      ParamsFilterKey.KEYWORD_TIME_FILTER,
      filter.keywordTimeFilter
    );
  }
  if (!!filter.orderBy) {
    searchParams.set(ParamsFilterKey.ORDER_BY, filter.orderBy);
  }
  if (!!filter.order) {
    searchParams.set(ParamsFilterKey.ORDER, filter.order);
  }
  if (Array.isArray(filter.schoolType) && filter.schoolType.length > 0) {
    searchParams.set(
      ParamsFilterKey.SCHOOL_TYPE,
      joinQueryValues(filter.schoolType)
    );
  }
  if (filter.fullTextSearch) {
    searchParams.set(ParamsFilterKey.FULL_TEXT_SEARCH, filter.fullTextSearch);
  }
  return searchParams.toString();
};
